import {
  CrossCircledIcon,
  DotsVerticalIcon,
  EraserIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { SendHorizonalIcon } from "lucide-react";
import * as React from "react";
import { useEditor } from "tldraw";

import {
  CanvasEditorMachine,
  setDebug,
} from "@/components/canvas-editor/canvas-editor-machine";
import { AuditLog, auditVerdict } from "@/components/canvas-editor/shapes/audit";
import {
  CopyLinkMenuItem,
  CopyUserDidMenuItem,
} from "@/components/CopyUserDidMenuItem";
import { Delayed } from "@/components/Delayed";
import { useEvent } from "@/components/hooks/useEvent";
import { PdsLsMenuItem } from "@/components/PdsLsMenuItem";
import {
  HiddenPostProvider,
  PreviewPost,
  PreviewPostBadge,
  PreviewPostMenuItems,
} from "@/components/PreviewFeed";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import { Separator } from "@/components/ui/separator";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { getHydratedPostQueryOptions } from "@/lib/api";
import { FeedVersion } from "@/lib/types";
import { cn, urlToAtUri } from "@/lib/utils";

/** Mirrors `RELATION_NOUN` in feed-processor's `app/emit_delivery.py`. Kept in the same
 *  words on both sides so the badge and the sentence under it do not name the same thing
 *  two different ways. */
const RELATION_NOUN: Record<string, string> = {
  parent: "parent post",
  quoted: "quoted post",
  root: "thread root",
};

function DebugPostResult({
  result,
  postUrl,
  menuActions,
  feedId,
}: {
  result?: AuditLog;
  postUrl: string;
  menuActions?: React.ReactNode;
  feedId?: number;
}) {
  const activePost = useQuery(getHydratedPostQueryOptions(postUrl));
  const atUri = urlToAtUri(postUrl);
  const isHidden = CanvasEditorMachine.useSelector(
    (s) => s.context.debugHiddenStatuses[atUri],
  );
  const isPresent = CanvasEditorMachine.useSelector(
    (s) => s.context.debugPresenceStatuses[atUri] === 1,
  );
  const isPending = CanvasEditorMachine.useSelector((s) =>
    s.context.pendingAuditResults.includes(postUrl),
  );
  // Whether the post matches is only half the answer on a feed whose `emit` redirects.
  // Absent (and so undefined) on every ordinary feed -- the server sends nothing rather
  // than a list of "delivered as itself".
  const delivery = CanvasEditorMachine.useSelector(
    (s) => s.context.debugDeliveryStatuses[atUri],
  );
  // The ROOT node's verdict, not `results[0]`, because only the audit tree carries
  // `undecided` -- and a post the manifest could not be evaluated against must not be
  // reported with the same confident "Failed" as one it genuinely rejected.
  const status = auditVerdict(result?.audit);
  const actor = CanvasEditorMachine.useActorRef();

  if (!activePost.data) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <PreviewPost
        record={{
          ...activePost.data,
          hidden: isHidden ?? false,
        }}
        isPresent={isPresent}
        isDimmed={!result}
        className={cn(
          !isPending &&
            (status === "success"
              ? "post-success"
              : status === "failure"
              ? "post-error"
              : undefined),
        )}
        details={
          isPending ? (
            <Spinner className="h-4 w-4" />
          ) : status === "success" && delivery?.outcome === "dropped" ? (
            // The verdict is genuinely "passed", so the plain "Passed" badge would not be
            // WRONG -- but its message, "Post would be visible on feed", is, and that is the
            // sentence the feed builder acts on. A match on an `emit: root` feed with no root
            // is discarded outright.
            <PreviewPostBadge message={delivery.explanation ?? ""}>
              Passed, not delivered
            </PreviewPostBadge>
          ) : status === "success" && delivery?.outcome === "redirected" ? (
            <PreviewPostBadge message={delivery.explanation ?? ""}>
              Passed, delivered as {RELATION_NOUN[delivery.emit] ?? delivery.emit}
            </PreviewPostBadge>
          ) : status === "success" ? (
            <PreviewPostBadge message="Post would be visible on feed">
              Passed
            </PreviewPostBadge>
          ) : status === "undecided" ? (
            // NOT "Failed". The engine answered `false` here only because a boolean array has
            // no third value -- it reported the post as undecided alongside. Saying the post
            // would be hidden would be stating a verdict nothing ever reached.
            <PreviewPostBadge message="Debug could not evaluate this post against one or more nodes, so there is no verdict either way">
              No verdict
            </PreviewPostBadge>
          ) : status === "failure" &&
            delivery?.outcome === "not_matched_but_present" ? (
            // "Failed" next to the present-on-feed tick reads as a contradiction, and it is
            // the exact pair that got this reported. It is not a contradiction: a sibling
            // matched and named this post.
            <PreviewPostBadge message={delivery.explanation ?? ""}>
              Failed, but in feed
            </PreviewPostBadge>
          ) : status === "failure" ? (
            <PreviewPostBadge message="Post would be hidden on feed">
              Failed
            </PreviewPostBadge>
          ) : undefined
        }
        actions={
          <DropdownMenu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="smIcon">
                <span className="sr-only">More actions</span>
                <DotsVerticalIcon />
              </Button>
            </MenuTrigger>
            <MenuContent sideOffset={12} align="start">
              <MenuItem
                onSelect={() =>
                  actor.send({
                    type: "REMOVE_AUDIT_ITEM",
                    url: postUrl,
                  })
                }
              >
                Remove from debug
              </MenuItem>
              <CopyUserDidMenuItem post={activePost.data} />
              <CopyLinkMenuItem post={activePost.data} />
              <PdsLsMenuItem post={activePost.data} />
              {feedId && (
                <PreviewPostMenuItems record={activePost.data} feedId={feedId} />
              )}
              {menuActions}
            </MenuContent>
          </DropdownMenu>
        }
      />
      {/* Visible text, deliberately not only the badge's tooltip. The last Debug report
          this codebase acted on was a red X whose only explanation was a `title` the
          reporter could not hover on mobile; repeating that for the one sentence a
          confused feed builder actually needs would be the same mistake twice. */}
      {!isPending && delivery?.notable && delivery.explanation && (
        <p className="bg-ambera-3 dark:bg-amberdarka-3 rounded p-2 text-xs text-slate-dim">
          {delivery.explanation}
        </p>
      )}
    </div>
  );
}

export function WorkbenchDebugPost({
  feedVersion,
  feedId,
}: {
  feedVersion: FeedVersion;
  feedId?: number;
}) {
  const editor = useEditor();
  const machine = CanvasEditorMachine.useActorRef();
  const runDebug = (urls: string[]) => {
    machine.send({
      type: "START_AUDIT",
      urls,
      manifest: feedVersion.manifest,
      editor,
      algo_id: feedId,
    });
  };
  const clearDebug = useEvent(() => {
    setDebug(editor, undefined);
    machine.send({ type: "SET_DEBUG_RESULT_URL" });
  });
  const audits = CanvasEditorMachine.useSelector((s) => s.context.debugResults);
  const message = CanvasEditorMachine.useSelector(
    (s) => s.context.debugMessage,
  );
  const error = CanvasEditorMachine.useSelector((s) => s.context.debugError);
  const onSubmit = () => {
    const input = document.getElementById("debug-post-url") as HTMLInputElement;
    const url = input.value.trim();
    if (!url || !feedVersion) return;
    machine.send({
      type: "START_AUDIT",
      urls: [url],
      manifest: feedVersion.manifest,
      editor,
      algo_id: feedId,
    });
    input.value = "";
  };

  const [searchValue, setSearchValue] = React.useState("");

  return (
    <div className="h-full flex flex-col overflow-hidden canvas-sidebar ">
      <div className="flex flex-col gap-6 w-full p-4">
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <Input
              name="post"
              placeholder="Enter a post URL"
              id="debug-post-url"
              onValueChange={setSearchValue}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onSubmit();
                }
              }}
            />
            <Button
              className="shrink-0"
              id="debug-post-url-button"
              type="button"
              size="icon"
              variant="action"
              onPress={onSubmit}
              disabled={searchValue.length === 0}
            >
              <span className="sr-only">Start audit</span>
              <SendHorizonalIcon />
            </Button>
          </div>
        </div>
      </div>
      <Separator />
      <div className="flex items-start justify-between gap-2 pr-4">
        <div className="min-w-0 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-start gap-2">
          <span className="py-4 pl-4 shrink-0">Results</span>
          {error ? (
            // `error` is a boolean flag -- the reason the audit failed lives in
            // `message`, which the branch below only renders when there ISN'T an
            // error. Rendering {error} here put a boolean in the DOM, which React
            // drops, so a failed audit showed a bare red cross and nothing else:
            // no reason, no tooltip, nothing to act on. Show the reason.
            <div className="flex min-w-0 items-start gap-1 py-4">
              <CrossCircledIcon className="mt-px shrink-0 text-red-10 dark:text-reddark-10" />
              <span className="text-red-dim text-xs break-words">
                {message || "Debug could not evaluate this post."}
              </span>
            </div>
          ) : (
            message && (
              <Delayed delay={1500}>
                <div className="flex items-center gap-2 py-4">
                  <span className="text-slate-dim text-xs">({message})</span>
                </div>
              </Delayed>
            )
          )}
        </div>
        {Object.keys(audits).length > 0 && (
          <div className="flex items-center gap-1">
            <SimpleTooltip message="Clear result highlights">
              <Button size="smIcon" variant="ghost" onPress={clearDebug}>
                <span className="sr-only">Clear result highlights</span>
              <EraserIcon />
              </Button>
            </SimpleTooltip>
            <SimpleTooltip message="Retry all">
              <Button
                size="smIcon"
                variant="ghost"
                onPress={() => {
                  const urls = Object.keys(audits);
                  runDebug(urls);
                }}
              >
                <ReloadIcon />
              </Button>
            </SimpleTooltip>
          </div>
        )}
      </div>
      <Separator />
      <HiddenPostProvider>
        <div className="min-h-0 flex-1 overflow-auto bg-slate-3 dark:bg-slatedark-3 gap-4 p-4 flex flex-col">
          {Object.entries(audits)
            .reverse()
            .map(([url, audit]) => {
              return (
                <div key={url} className="">
                  <DebugPostResult
                    result={audit}
                    postUrl={url}
                    feedId={feedId}
                    menuActions={
                      <>
                        <MenuItem onSelect={() => runDebug([url])}>
                          Retry debug
                        </MenuItem>
                        {audit && (
                          <MenuItem
                            onSelect={() => {
                              setDebug(editor, audit);
                              machine.send({
                                type: "SET_DEBUG_RESULT_URL",
                                url,
                              });
                            }}
                          >
                            Apply result to canvas
                          </MenuItem>
                        )}
                      </>
                    }
                  />
                </div>
              );
            })}
        </div>
      </HiddenPostProvider>
    </div>
  );
}
